import React, { useState, useCallback } from 'react';
import {
  Paper, Group, Stack, Text, Title, Badge, Button, Select, Loader, Alert
} from '@mantine/core';
import { TextInput } from '@mantine/core';
import { DataGrid } from '@mui/x-data-grid';
import { IconRefresh, IconAlertCircle, IconDownload } from '@tabler/icons-react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import { getAuthScopes } from '../../../config/scopes';
import config from '../../../config';
import PriceExplainPanel from './PriceExplainPanel';

const DARK   = 'var(--mantine-color-dark-7)';
const BORDER = 'var(--mantine-color-dark-4)';
const TEXT   = 'var(--mantine-color-text)';

const GRID_SX = {
  border: 0,
  '--DataGrid-containerBackground': `${DARK} !important`,
  '--DataGrid-rowBackground':       `${DARK} !important`,
  '--DataGrid-footerBackground':    `${DARK} !important`,
  color: TEXT,
  bgcolor: DARK,
  '&.MuiDataGrid-root .MuiDataGrid-container--top [role=row]': {
    backgroundColor: `${DARK} !important`,
    color: `${TEXT} !important`,
  },
  '& .MuiDataGrid-columnHeaderTitle': { color: TEXT, fontWeight: 600 },
  '& .MuiDataGrid-columnSeparator':   { color: `${BORDER} !important` },
  '& .MuiDataGrid-filler':            { backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-row':               { backgroundColor: `${DARK} !important`, color: `${TEXT} !important` },
  '& .MuiDataGrid-row:hover':         { backgroundColor: 'var(--mantine-color-dark-6) !important' },
  '& .MuiDataGrid-cell':              { borderColor: BORDER, color: TEXT, backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-footerContainer':   { backgroundColor: `${DARK} !important`, borderColor: BORDER, color: TEXT },
  '& .MuiTablePagination-root':       { color: TEXT },
  '& .MuiTablePagination-selectIcon': { color: TEXT },
  '& .MuiSvgIcon-root':               { color: `${TEXT} !important` },
  '& .MuiDataGrid-overlay':           { backgroundColor: `${DARK} !important`, color: TEXT },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none !important' },
};

const fmt = (v) =>
  v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const trim    = (v) => { const s = v == null ? '' : String(v).trim(); return s || '—'; };
const fmtDate = (v) => (v == null ? '—' : new Date(v).toLocaleDateString('en-US'));

// Columns mirror the actual Rotabull feed (GDB_01_7.dbo.VIEW_ROTABULL_PRICING). The "why"
// (DOS, discount, SSP) lives in the detail panel below, fetched on row click.
const COLUMNS = [
  { field: 'part_number',     headerName: 'Part',        width: 150, sortable: true,  renderCell: ({ value }) => trim(value) },
  { field: 'condition',       headerName: 'Cond',        width: 70,  sortable: true,  renderCell: ({ value }) => trim(value) },
  {
    field: 'price_in_usd', headerName: 'Price (USD)', width: 120, sortable: true, align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => fmt(value),
  },
  { field: 'serial_number',   headerName: 'Serial',      width: 130, sortable: true,  renderCell: ({ value }) => trim(value) },
  { field: 'stock_line',      headerName: 'Stock Line',  width: 100, sortable: true,  align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => (value == null ? '—' : value) },
  { field: 'company_category',headerName: 'Category',    width: 110, sortable: true,  renderCell: ({ value }) => trim(value) },
  { field: 'trace_to',        headerName: 'Trace To',    width: 150, sortable: false, renderCell: ({ value }) => trim(value) },
  { field: 'tag_source',      headerName: 'Tag Source',  width: 150, sortable: false, renderCell: ({ value }) => trim(value) },
  { field: 'tag_date',        headerName: 'Tag Date',    width: 110, sortable: false, renderCell: ({ value }) => fmtDate(value) },
];

export default function RotabullPricing() {
  const [partNumber,   setPartNumber]   = useState('');
  const [condition,    setCondition]    = useState('');
  const [priceStatus,  setPriceStatus]  = useState('');
  const [lineType,     setLineType]     = useState('');
  const [page,         setPage]         = useState(0);
  const [pageSize,     setPageSize]     = useState(100);
  const [sortModel,    setSortModel]    = useState([{ field: 'part_number', sort: 'asc' }]);
  const [selected,     setSelected]     = useState(null);
  const [exporting,    setExporting]    = useState(false);

  const { instance, accounts } = useMsal();

  // Shared filter params (used by both the grid URL and the CSV export).
  const filterParams = useCallback((p) => {
    if (partNumber)  p.set('partNumber',  partNumber);
    if (condition)   p.set('condition',   condition);
    if (priceStatus) p.set('priceStatus', priceStatus);
    if (lineType)    p.set('lineType',    lineType);
    return p;
  }, [partNumber, condition, priceStatus, lineType]);

  const buildUrl = useCallback((over = {}) => {
    const pg = over.page      ?? page;
    const ps = over.pageSize  ?? pageSize;
    const sm = over.sortModel ?? sortModel;
    const p = new URLSearchParams({ page: pg, pageSize: ps });
    if (sm[0]) { p.set('sortField', sm[0].field); p.set('sortDir', sm[0].sort); }
    filterParams(p);
    return `${config.apiBaseUrl}/api/rotabull/price-lines?${p}`;
  }, [page, pageSize, sortModel, filterParams]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const scopes = getAuthScopes();
      const tok = await instance.acquireTokenSilent({ scopes, account: accounts[0] });
      const p = new URLSearchParams();
      if (sortModel[0]) { p.set('sortField', sortModel[0].field); p.set('sortDir', sortModel[0].sort); }
      filterParams(p);
      const resp = await axios.get(`${config.apiBaseUrl}/api/rotabull/price-lines/export?${p}`, {
        headers: { Authorization: `Bearer ${tok.accessToken}` },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `rotabull-pricing-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('CSV export failed', e);
    } finally {
      setExporting(false);
    }
  }, [instance, accounts, sortModel, filterParams]);

  const [url, setUrl] = useState(buildUrl);

  const { data, isLoading, error, refetch } = useAuthenticatedData(url);

  const applyFilters = () => {
    setPage(0);
    setSelected(null);
    setUrl(buildUrl({ page: 0 }));
  };

  const rows  = (data?.rows ?? []).map((r, i) => ({ id: i, ...r }));
  const total = data?.total ?? 0;

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between">
        <Title order={3}>Rotabull Pricing</Title>
        <Group gap="xs">
          <Button
            size="xs" variant="light" leftSection={<IconDownload size={14} />}
            onClick={handleExport} loading={exporting}
          >
            Download CSV
          </Button>
          <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />} onClick={refetch}>
            Refresh
          </Button>
        </Group>
      </Group>

      <Paper withBorder p="sm" radius="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            label="Part Number" placeholder="e.g. 1234" size="xs" style={{ width: 160 }}
            value={partNumber} onChange={e => setPartNumber(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
          />
          <Select
            label="Condition" size="xs" clearable style={{ width: 120 }}
            value={condition || null} onChange={v => setCondition(v ?? '')}
            data={['AR', 'OH', 'SV', 'NS', 'NE']}
          />
          <Select
            label="Price Status" size="xs" clearable style={{ width: 130 }}
            value={priceStatus || null} onChange={v => setPriceStatus(v ?? '')}
            data={[
              { value: 'priced',   label: 'Priced' },
              { value: 'unpriced', label: 'Unpriced' },
            ]}
          />
          <Select
            label="Customer Type" size="xs" clearable style={{ width: 190 }}
            value={lineType || null} onChange={v => setLineType(v ?? '')}
            data={[
              { value: 'serialized', label: 'Airlines & Key Customers' },
              { value: 'part',       label: 'Everyone Else' },
            ]}
          />
          <Button size="xs" style={{ alignSelf: 'flex-end' }} onClick={applyFilters}>
            Apply
          </Button>
        </Group>
      </Paper>

      {error && (
        <Alert icon={<IconAlertCircle size={14} />} color="red" variant="light">
          Failed to load pricing data.
        </Alert>
      )}

      <Paper withBorder radius="md" style={{ height: 560, width: '100%', overflow: 'hidden' }}>
        {isLoading && (
          <Group justify="center" p="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading…</Text>
          </Group>
        )}
        {!isLoading && (
          <DataGrid
            rows={rows}
            columns={COLUMNS}
            rowCount={total}
            paginationMode="server"
            sortingMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
              setUrl(buildUrl({ page: p, pageSize: ps }));
            }}
            sortModel={sortModel}
            onSortModelChange={sm => {
              setSortModel(sm);
              setPage(0);
              setUrl(buildUrl({ page: 0, sortModel: sm }));
            }}
            onRowClick={({ row }) => setSelected(prev => (prev?.id === row.id ? null : row))}
            rowSelectionModel={selected ? [selected.id] : []}
            pageSizeOptions={[50, 100, 250]}
            disableRowSelectionOnClick={false}
            density="compact"
            sx={{ ...GRID_SX, '& .MuiDataGrid-row': { ...GRID_SX['& .MuiDataGrid-row'], cursor: 'pointer' } }}
          />
        )}
      </Paper>

      <PriceExplainPanel selected={selected} />
    </Stack>
  );
}
